import React, { useEffect, useMemo } from 'react';

import { useHistory } from 'react-router-dom';
import { Button, Notification, NotificationsGroup } from '@vwfs-bronson/bronson-react';
import { useCmsContent } from '@cp-shared-19/frontend-integration';
import { RedirectBannerDto } from 'cpes-common';
import { useDispatch } from 'react-redux';
import { useAnalyticsActionTracker } from '@cp-shared-19/frontend-ui';

import { withLoadingAndNoConnectionHandler } from '../integration-wrapper';
import { fetchRedirectionBanner } from './RedirectionBannerContentSlice';
import { RedirectionBannerState, setDoNotShowAgain, setIsRedirectionBannerVisible } from './RedirectionBannerSlice';
import { RootState } from '../../store/rootReducer';
import { isNewDomainAcknowledgedCookieName } from '../../config';
import { getUrlSearchParams } from '../../utils';

type RedirectionBannerProps = {
    content: RedirectBannerDto;
    redirectionBanner: RedirectionBannerState;
};

const RedirectionBanner: React.FC<RedirectionBannerProps> = ({ content, redirectionBanner }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { onAction: onChangeContextAction } = useAnalyticsActionTracker('onChangeContext');

    const headline = content && content?.headline ? content.headline : '';
    const buttonText = content && content?.buttonText ? content.buttonText : '';
    const description = content && content?.description ? content.description : '';
    const isNewDomainAcknowledged = useMemo(
        () =>
            document.cookie
                .split('; ')
                .find(cookie => cookie.startsWith(`${isNewDomainAcknowledgedCookieName}=`))
                ?.split('=')[1] === '1',
        [document.cookie],
    );

    const { isVisible, doNotShowAgain } = redirectionBanner;

    useEffect(
        function handleIsNewDomainAcknowledged() {
            const searchParams = getUrlSearchParams();
            const source = searchParams.get('source');
            if (source === 'fsonline') {
                onChangeContextAction('Redirect=fsonline.es');
                dispatch(setIsRedirectionBannerVisible(!isNewDomainAcknowledged && !doNotShowAgain));
                searchParams.delete('source');

                history.replace({
                    pathname: location.pathname,
                    search: searchParams.toString() ? `?${searchParams.toString()}` : '',
                    hash: location.hash,
                });
            }
        },
        [isNewDomainAcknowledged, setDoNotShowAgain, setIsRedirectionBannerVisible],
    );

    const onCloseClick = () => dispatch(setIsRedirectionBannerVisible(false));

    const onDoNotShowAgainButtonOnClick = () => {
        document.cookie = `${isNewDomainAcknowledgedCookieName}=1; Max-Age=31536000; Path=/; SameSite=Lax`;
        dispatch(setDoNotShowAgain());
    };

    return (
        <NotificationsGroup ariaLabel="Test">
            <Notification
                title={headline}
                status="info"
                buttons={[
                    <Button key="1" onClick={onDoNotShowAgainButtonOnClick} secondary type="button">
                        {buttonText}
                    </Button>,
                ]}
                onCloseClick={onCloseClick}
                visible={isVisible}
            >
                <p>{description}</p>
            </Notification>
        </NotificationsGroup>
    );
};

const RedirectionBannerWithWrappers = withLoadingAndNoConnectionHandler(RedirectionBanner);

export const LandingPageRedirectionBanner: React.FC = () => {
    const {
        content: { cmsContent, isLoading, loadingError },
        redirectionBanner,
    } = useCmsContent(fetchRedirectionBanner, (state: RootState) => ({
        content: state.content.redirectionBanner,
        redirectionBanner: state.redirectionBanner,
    }));

    return (
        <RedirectionBannerWithWrappers
            isLoading={isLoading}
            hasError={!!loadingError}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            content={cmsContent!}
            redirectionBanner={redirectionBanner}
        />
    );
};
